import type { CSSProperties, RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

type UseDestinationPickerPopoverArgs = {
    wrapperRef: RefObject<HTMLDivElement | null>;
    isOpen: boolean;
    hasDrilldown: boolean;
    onClose: () => void;
    onBack: () => void;
};

export const useDestinationPickerPopover = ({ wrapperRef, isOpen, hasDrilldown, onClose, onBack }: UseDestinationPickerPopoverArgs) => {
    const [popoverStyle, setPopoverStyle] = useState<CSSProperties>();

    const updatePopoverStyle = useCallback((): void => {
        const wrapper = wrapperRef.current;
        const searchPanel = wrapper?.closest('form');
        if (!wrapper || !searchPanel) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const searchPanelRect = searchPanel.getBoundingClientRect();

        setPopoverStyle({
            left: searchPanelRect.left - wrapperRect.left,
            width: searchPanelRect.width,
        });
    }, [wrapperRef]);

    useEffect(() => {
        if (!isOpen) return undefined;

        updatePopoverStyle();

        const handleClickOutside = (event: MouseEvent): void => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                onClose();
            }
        };

        const handleEscape = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') return;
            if (hasDrilldown) {
                onBack();
            } else {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        window.addEventListener('resize', updatePopoverStyle);
        window.addEventListener('scroll', updatePopoverStyle, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
            window.removeEventListener('resize', updatePopoverStyle);
            window.removeEventListener('scroll', updatePopoverStyle, true);
        };
    }, [hasDrilldown, isOpen, onBack, onClose, updatePopoverStyle, wrapperRef]);

    return { popoverStyle, updatePopoverStyle };
};
